import { useLocalization } from '@lib/localization';
import SPInspection from '@lib/spproject/SPInspection';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type DetailedPOI } from './Detail';

interface OverTimeChartProps {
  pois: DetailedPOI[];
}

interface ChartDataPoint {
  date: string;
  ok: number;
  warning: number;
  error: number;
  notChecked: number;
}

const OvertimeChart = ({ pois }: OverTimeChartProps) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();

  const chartData = useMemo(() => {
    if (!pois || pois.length === 0) return [];

    const dateMap = new Map<string, ChartDataPoint>();

    pois.forEach((poi) => {
      const step = poi.entry.steps[poi.entry.stepIndex];
      if (!step) return;

      const dateKey = new Date(poi.info.dateModified).toISOString().split('T')[0];

      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, {
          date: dateKey,
          ok: 0,
          warning: 0,
          error: 0,
          notChecked: 0,
        });
      }

      const dataPoint = dateMap.get(dateKey)!;

      const status = SPInspection.getPOIStatus(poi.entry);

      if (status === 'Ok') dataPoint.ok++;
      else if (status === 'Error') dataPoint.error++;
      else if (status === 'Warning') dataPoint.warning++;
      else dataPoint.notChecked++;
    });

    const result = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    if (result.length === 1) {
      result.unshift({ ...result[0] });
    }

    return result;
  }, [pois]);

  const isOkLineExist = chartData.some(data => Boolean(data.ok));
  const isWarningLineExist = chartData.some(data => Boolean(data.warning));
  const isErrorLineExist = chartData.some(data => Boolean(data.error));
  const isNotCheckedLineExist = chartData.some(data => Boolean(data.notChecked));

  const getMaxValue = (data: ChartDataPoint[]) =>
    Math.max(...data.flatMap(item => [item.ok, item.warning, item.error, item.notChecked]));

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric' });

  const maxValue = getMaxValue(chartData);

  return (
    <ResponsiveContainer width="100%" height="100%" style={{ marginTop: '36px' }}>
      <LineChart
        data={chartData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tickFormatter={formatDate}
        />
        <YAxis axisLine={false} tickLine={false} allowDecimals={false} domain={[0, maxValue]} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1E1E2D',
            border: '1px solid #333',
            borderRadius: 8,
            color: '#F1F1F2',
            fontSize: 12,
          }}
          labelStyle={{
            color: '#FFFFFF',
            fontWeight: 'bold',
          }}
          itemStyle={{
            color: '#E1E1E2',
          }}
        />
        {isOkLineExist && (
          <Line
            type="monotone"
            dataKey="ok"
            name={t('Common_Successful')}
            stroke={noticeColors.Ok}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
            style={{ transform: 'translateY(-3px)' }}
          />
        )}
        {isWarningLineExist && (
          <Line
            type="monotone"
            dataKey="warning"
            name={t('Common_Warning')}
            stroke={noticeColors.Warning}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
            style={{ transform: 'translateY(-2px)' }}
          />
        )}
        {isErrorLineExist && (
          <Line
            type="monotone"
            dataKey="error"
            name={t('Common_Error')}
            stroke={noticeColors.Error}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
            style={{ transform: 'translateY(-1px)' }}
          />
        )}
        {isNotCheckedLineExist && (
          <Line
            type="monotone"
            dataKey="notChecked"
            name={t('Common_NotChecked')}
            stroke={noticeColors.NotChecked}
            strokeWidth={3}
            dot={false}
            activeDot={{ r: 8 }}
          />
        )}

        <defs>
          <linearGradient id="colorOk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={noticeColors.Ok} stopOpacity={0.1} />
            <stop offset="95%" stopColor={noticeColors.Ok} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorWarning" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={noticeColors.Warning} stopOpacity={0.1} />
            <stop offset="95%" stopColor={noticeColors.Warning} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorError" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={noticeColors.Error} stopOpacity={0.1} />
            <stop offset="95%" stopColor={noticeColors.Error} stopOpacity={0} />
          </linearGradient>
        </defs>
      </LineChart>
    </ResponsiveContainer>
  );
};

export default OvertimeChart;
